/** Embalados hoje por pessoa (usuário embalador do Tiny), dentro dos filtros. A gerente dá nome aos ids pelo lápis. */
import { useState } from 'react'
import {
  Alert, Box, Button, CardActionArea, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material'
import { Hicon } from '../../components/Hicon'
import type { AbrirLista, Pedido } from './types'
import { COR_SITUACAO } from './logic'
import { salvarNome } from './fonte'

type Pessoa = { id: string | null; nome: string; pedidos: Pedido[] }

function agrupar(pedidos: Pedido[], hoje: string): Pessoa[] {
  const m = new Map<string, Pessoa>()
  for (const p of pedidos) {
    if (p.situacao !== 3 || p.data_checkout !== hoje) continue
    const k = p.embalador_id ?? ''
    const item = m.get(k) ?? { id: p.embalador_id, nome: p.embalador_nome, pedidos: [] }
    item.pedidos.push(p)
    m.set(k, item)
  }
  return [...m.values()].sort((a, b) => b.pedidos.length - a.pedidos.length)
}

export function PorPessoa({ pedidos, hoje, podeEditar, abrirLista, onSalvo }: {
  pedidos: Pedido[]; hoje: string; podeEditar: boolean; abrirLista: AbrirLista; onSalvo: () => void
}) {
  const pessoas = agrupar(pedidos, hoje)
  const max = Math.max(1, ...pessoas.map((x) => x.pedidos.length))
  const [editando, setEditando] = useState<Pessoa | null>(null)
  const [nome, setNome] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const abrirEdicao = (x: Pessoa) => {
    setEditando(x)
    setNome(x.nome.startsWith('Sem nome') ? '' : x.nome)
    setErro(null)
  }
  const salvar = async () => {
    if (!editando?.id) return
    setSalvando(true)
    try {
      await salvarNome(editando.id, nome.trim())
      setEditando(null)
      onSalvo()
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setSalvando(false)
    }
  }

  if (!pessoas.length) return <Typography color="text.secondary" sx={{ py: 2 }}>Ninguém embalou hoje com esses filtros (a data máx. não conta aqui).</Typography>
  return (
    <>
      <Stack spacing={0.25}>
        {pessoas.map((x) => {
          const semNome = !x.id || x.nome.startsWith('Sem nome')
          return (
            <Stack key={x.id ?? 'sem'} direction="row" spacing={0.5} alignItems="center">
              <CardActionArea onClick={() => abrirLista(`Embalados hoje por ${x.nome}`, x.pedidos)} sx={{ borderRadius: 2, px: 0.75, py: 0.4, flex: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={{ width: 150, flexShrink: 0, font: '700 15px Outfit', color: semNome ? 'text.disabled' : undefined }} noWrap>{x.nome}</Typography>
                  <Box sx={{ flex: 1, height: 30, bgcolor: 'action.hover', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${(x.pedidos.length / max) * 100}%`, bgcolor: semNome ? '#94a3b8' : COR_SITUACAO[3], borderRadius: 2 }} />
                  </Box>
                  <Typography sx={{ width: 56, textAlign: 'right', font: '800 26px Outfit', color: '#065f46', fontVariantNumeric: 'tabular-nums' }}>{x.pedidos.length}</Typography>
                </Stack>
              </CardActionArea>
              {podeEditar && x.id ? (
                <Tooltip title="Dar nome a este usuário do Tiny">
                  <IconButton size="small" aria-label={`editar nome de ${x.nome}`} onClick={() => abrirEdicao(x)}><Hicon name="pencil" sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
              ) : <Box sx={{ width: 34 }} />}
            </Stack>
          )
        })}
      </Stack>
      <Dialog open={!!editando} onClose={() => setEditando(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Nome do embalador</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Usuário do Tiny id {editando?.id}</Typography>
          <TextField autoFocus fullWidth size="small" label="Nome" value={nome} onChange={(e) => setNome(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 40 } }}
            onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim()) void salvar() }} />
          {erro && <Alert severity="error" sx={{ mt: 1.5 }}>Não foi possível salvar: {erro}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditando(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!nome.trim() || salvando} onClick={() => void salvar()}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
